import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, CheckCircle2, Hash, Loader2, Phone, Plus, Search, UserRound, Users, BadgeCheck, Home as HomeIcon, Sparkles, X } from 'lucide-react';
import { API, errMsg } from './api';

const CATS = [
  { key: 'contacts_all', label: 'All contacts', sub: 'Every client on a pipeline deal', Icon: Users },
  { key: 'contacts_approved', label: 'Approved contacts', sub: 'Loan currently approved', Icon: BadgeCheck },
  { key: 'contacts_settled', label: 'Settled contacts', sub: 'Loan has settled', Icon: HomeIcon },
  { key: 'leads_all', label: 'All leads', sub: 'Every lead source', Icon: Sparkles },
];

const CategoryCard = ({ c, on, stat, toggle }) => (
  <button type="button" onClick={toggle} className={`bsx-cat${on ? ' on' : ''}`} data-testid={`bsx-cat-${c.key}`} aria-pressed={on}>
    <span className="bsx-cat-ic"><c.Icon size={17} /></span>
    <span className="bsx-cat-body"><b>{c.label}</b><small>{c.sub}</small></span>
    <span className="bsx-cat-n"><b className="num">{stat ? stat.reachable : '–'}</b><small>{stat ? `of ${stat.total}` : ''}</small></span>
    <span className="bsx-tick">{on && <Check size={13} strokeWidth={3} />}</span>
  </button>
);

// Search-and-pick for individual contacts or leads; contacts missing a mobile can have one added inline.
const Picker = ({ kind, headers, picked, setPicked }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [adding, setAdding] = useState(null);
  const [mob, setMob] = useState('');
  const t = useRef();
  useEffect(() => {
    clearTimeout(t.current);
    t.current = setTimeout(() => axios.get(`${API}/search`, { ...headers, params: { kind, q } }).then(({ data }) => setRes(data.results)).catch(() => setRes([])), 220);
    return () => clearTimeout(t.current);
  }, [q, kind, headers]);
  const toggle = (r) => setPicked((p) => (p.some((x) => x.id === r.id) ? p.filter((x) => x.id !== r.id) : [...p, r]));
  const saveMobile = async (r) => {
    try { const { data } = await axios.post(`${API}/contacts/${r.id}/mobile`, { phone: mob }, headers); toast.success(`Mobile saved for ${r.name}`); setAdding(null); setMob(''); setRes((rs) => rs.map((x) => (x.id === r.id ? { ...x, phone: data.phone } : x))); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bsx-picker" data-testid={`bsx-picker-${kind}`}>
      <div className="bsx-search"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${kind} by name or number`} data-testid={`bsx-search-${kind}`} />{q && <button type="button" onClick={() => setQ('')} aria-label="Clear"><X size={14} /></button>}</div>
      {picked.length > 0 && <div className="bsx-chips">{picked.map((p) => <span key={p.id} className="bsx-chip">{p.name}<button type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.name}`}><X size={12} /></button></span>)}</div>}
      <ul className="bsx-results">
        {res === null && <li className="bsx-empty"><Loader2 size={16} className="animate-spin" /></li>}
        {res?.length === 0 && <li className="bsx-empty">No {kind} found</li>}
        {res?.map((r) => { const on = picked.some((x) => x.id === r.id); return (
          <li key={r.id}>
            <button type="button" disabled={!r.phone || r.opted} onClick={() => toggle(r)} className={`bsx-row${on ? ' on' : ''}`} data-testid={`bsx-result-${r.id}`}>
              <span className="bsx-av">{(r.first_name || r.name || '?')[0]}</span>
              <span className="bsx-row-body"><b>{r.name}</b><small>{r.phone || 'No mobile on file'} · {r.status || r.source}</small></span>
              {r.opted ? <em className="bsx-tag rose">Opted out</em> : r.phone ? <span className="bsx-tick">{on && <Check size={13} strokeWidth={3} />}</span> : null}
            </button>
            {!r.phone && kind === 'contacts' && (adding === r.id
              ? <form className="bsx-addmob" onSubmit={(e) => { e.preventDefault(); saveMobile(r); }}><Phone size={13} /><input autoFocus value={mob} onChange={(e) => setMob(e.target.value)} placeholder="04xx xxx xxx" data-testid={`bsx-mobile-input-${r.id}`} /><button type="submit" data-testid={`bsx-mobile-save-${r.id}`}>Save</button><button type="button" onClick={() => setAdding(null)}>Cancel</button></form>
              : <button type="button" className="bsx-addmob-btn" onClick={() => { setAdding(r.id); setMob(''); }} data-testid={`bsx-mobile-add-${r.id}`}><Plus size={12} />Add mobile</button>)}
          </li>
        ); })}
      </ul>
    </div>
  );
};

export const RecipientsStep = ({ overview, headers, aud, setAud }) => {
  const [mode, setMode] = useState(null);
  const stats = overview?.audiences || {};
  const toggleCat = (k) => setAud((a) => ({ ...a, categories: a.categories.includes(k) ? a.categories.filter((x) => x !== k) : [...a.categories, k] }));
  const manualCount = aud.manual.split(/[,;\n\r]+/).filter((x) => x.trim()).length;
  return (
    <div className="bsx-step" data-testid="bsx-step-recipients">
      <div className="bsx-cats">{CATS.map((c) => <CategoryCard key={c.key} c={c} on={aud.categories.includes(c.key)} stat={stats[c.key]} toggle={() => toggleCat(c.key)} />)}</div>
      <p className="bsx-note"><CheckCircle2 size={13} />Counts show people with a valid, subscribed mobile. Pick as many groups as you like. Anyone in more than one group gets the message once.</p>
      <div className="bsx-more">
        {[['contacts', 'Individual contacts', UserRound, aud.contacts.length], ['leads', 'Individual leads', Sparkles, aud.leads.length], ['manual', 'Phone numbers', Hash, manualCount]].map(([k, label, Ic, n]) => (
          <button type="button" key={k} className={`bsx-more-btn${mode === k ? ' on' : ''}`} onClick={() => setMode(mode === k ? null : k)} data-testid={`bsx-add-${k}`}><Ic size={15} />{label}{n ? <span className="bsx-n">{n}</span> : <Plus size={14} className="bsx-plus" />}</button>
        ))}
      </div>
      {mode === 'contacts' && <Picker kind="contacts" headers={headers} picked={aud.contacts} setPicked={(f) => setAud((a) => ({ ...a, contacts: typeof f === 'function' ? f(a.contacts) : f }))} />}
      {mode === 'leads' && <Picker kind="leads" headers={headers} picked={aud.leads} setPicked={(f) => setAud((a) => ({ ...a, leads: typeof f === 'function' ? f(a.leads) : f }))} />}
      {mode === 'manual' && (
        <div className="bsx-picker">
          <textarea value={aud.manual} onChange={(e) => setAud((a) => ({ ...a, manual: e.target.value }))} rows={4} placeholder={'0412 345 678\n+61 400 111 222\nOne per line, or separate with commas'} data-testid="bsx-manual" className="bsx-textarea" />
        </div>
      )}
    </div>
  );
};
